import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  Award, Globe, BookOpen, Users, Zap, Target,
  CheckCircle
} from 'lucide-react';

const TrainingBenefitsInnovative: React.FC = () => {
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true });

  const benefits = [
    {
      icon: Award,
      title: "Advanced Counselling Expertise",
      fullDescription: "Dive deep into advanced therapeutic techniques that transform your practice. Our comprehensive training program equips you with cutting-edge methodologies, evidence-based interventions, and nuanced clinical skills that set you apart in the mental health profession.",
      keyPoints: [
        "Evidence-based intervention strategies",
        "Advanced diagnostic techniques",
        "Specialized therapeutic approaches"
      ],
      color: "bg-white",
      highlightColor: "text-[#8E44AD]"
    },
    {
      icon: Globe,
      title: "Holistic Approach",
      fullDescription: "Embrace a comprehensive approach that integrates traditional wisdom with contemporary psychological frameworks. Learn to treat the whole person—mind, body, and spirit—through interconnected therapeutic modalities.",
      keyPoints: [
        "Mind-body integration techniques",
        "Cross-cultural psychological perspectives",
        "Integrative healing methodologies"
      ],
      color: "bg-[#E2B010]/10",
      highlightColor: "text-[#E2B949]"
    },
    {
      icon: BookOpen,
      title: "Continuous Learning",
      fullDescription: "Stay at the forefront of mental health research and practice with our commitment to ongoing professional development. Access exclusive resources, research materials, and continuous education opportunities.",
      keyPoints: [
        "Updated research access",
        "Quarterly professional workshops",
        "Comprehensive learning resources"
      ],
      color: "bg-[#C09C73]/10",
      highlightColor: "text-[#434333]"
    },
    {
      icon: Users,
      title: "Peer Networking",
      fullDescription: "Build a powerful professional network with like-minded mental health professionals. Our program creates opportunities for collaboration, peer support, and meaningful connections.",
      keyPoints: [
        "Collaborative learning environments",
        "Professional mentorship programs",
        "Interdisciplinary networking events"
      ],
      color: "bg-[#E2B010]/10",
      highlightColor: "text-[#8E44AD]"
    },
    {
      icon: Zap,
      title: "Career Acceleration",
      fullDescription: "Propel your professional journey with credentials and skills that open doors to advanced clinical roles, specializations, and leadership opportunities in mental health.",
      keyPoints: [
        "Advanced certification tracks",
        "Professional credentialing",
        "Career development support"
      ],
      color: "bg-[#A1ADA5]/10",
      highlightColor: "text-[#C09C73]"
    },
    {
      icon: Target,
      title: "Personalized Mentorship",
      fullDescription: "Receive individualized guidance from experienced therapists who are committed to your professional growth. Our mentorship program provides tailored support to refine your clinical skills and therapeutic approach.",
      keyPoints: [
        "One-on-one mentoring sessions",
        "Personalized skill development",
        "Expert clinical supervision"
      ],
      color: "bg-[#434333]/10",
      highlightColor: "text-[#E2B010]"
    }
  ];


  const toggleCard = (index: number) => {
    setActiveCardIndex((prevIndex) => prevIndex === index ? null : index);
  };

  const VideoModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      onClick={() => setIsVideoModalOpen(false)}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="w-full max-w-4xl aspect-video bg-white rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gray-100 p-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">Greh Pravesh Therapy Training Overview</h3>
          <button
            onClick={() => setIsVideoModalOpen(false)}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <p className="text-gray-500">Video Placeholder</p>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="font-gentium-book-plus-regular py-16 relative" style={{
      backgroundImage: `url('/images/team-bg.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      {isVideoModalOpen && <VideoModal />}

      <div className="container mx-auto px-4">
        <motion.h1
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold text-center mb-16 text-[#434333]"
        >
          Transform Your Therapeutic Practice
        </motion.h1>

        <div 
          ref={containerRef}
          className="grid md:grid-cols-3 gap-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ 
                delay: isInView ? index * 0.1 : 0,
                duration: 0.5
              }}
              onClick={() => toggleCard(index)}
              className={`
                cursor-pointer rounded-xl p-6 transition-all duration-300 
                ${benefit.color} hover:shadow-xl
                ${activeCardIndex === index ? 'ring-4 ring-[#E2B010]/50' : ''}
                relative
              `}
            >
              <div className="flex items-center mb-4">
                <benefit.icon
                  className={`mr-4 ${benefit.highlightColor}`}
                  size={40}
                />
                <h2 className="text-2xl font-semibold text-[#434333]">
                  {benefit.title}
                </h2>
              </div>

              <AnimatePresence>
                {activeCardIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="text-[#A1ADA5] mb-4">{benefit.fullDescription}</p>
                    <div className="space-y-2">
                      {benefit.keyPoints.map((point, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckCircle
                            className={`mr-2 ${benefit.highlightColor}`}
                            size={20}
                          />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 1 }}
          className="text-center mt-12"
        >
          <p className="text-xl text-amber-800 italic max-w-2xl mx-auto">
            "Transform your therapeutic practice, elevate your professional journey, and make a meaningful impact in mental health."
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TrainingBenefitsInnovative;